import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  useTheme
} from '@mui/material';
import {
  Event as EventIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  Edit as EditIcon
} from '@mui/icons-material';

interface Event {
  id: string;
  name: string;
  description?: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  location: string;
  isMandatory: boolean;
  isRegistered: boolean;
  registrationStatus: string;
  dressCode?: string;
  imageUrl?: string;
  registrationCount?: number;
  maxCapacity?: number;
}

interface ChangeRequest {
  id: string;
  eventId?: string;
  requestType: 'event_change' | 'size_change';
  action?: string;
  status: 'pending' | 'approved' | 'denied';
  createdAt: string;
}

interface GuestEventScheduleProps {
  events: Event[];
  pendingRequests?: ChangeRequest[];
  onRefresh: () => void;
  showAllEvents?: boolean;
}

interface EventChangeRequest {
  eventId: string;
  action: 'register' | 'unregister';
  reason?: string;
}

export const GuestEventSchedule: React.FC<GuestEventScheduleProps> = ({ events, pendingRequests = [], onRefresh, showAllEvents = true }) => {
  const theme = useTheme();
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [changeAction, setChangeAction] = useState<'register' | 'unregister'>('register');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  // Helper to check if event has pending request
  const hasPendingRequest = (eventId: string) => {
    return pendingRequests.some(
      req => req.eventId === eventId && req.requestType === 'event_change' && req.status === 'pending'
    );
  };

  const handleImageError = (eventId: string) => {
    setImageErrors(prev => ({ ...prev, [eventId]: true }));
  };

  const getEventImageUrl = (event: Event) => {
    if (imageErrors[event.id] || !event.imageUrl) {
      return '/sample-media/event-placeholder.svg';
    }
    return event.imageUrl;
  };

  const formatDate = (dateString: string) => {
    // Parse YYYY-MM-DD as local date to avoid timezone shifts
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleRequestChange = (event: Event) => {
    setSelectedEvent(event);
    setChangeAction(event.isRegistered ? 'unregister' : 'register');
    setReason('');
    setError(null);
    setChangeDialogOpen(true);
  };

  const handleSubmitChange = async () => {
    if (!selectedEvent) return;

    try {
      setSubmitting(true);
      setError(null);

      const requestData: EventChangeRequest = {
        eventId: selectedEvent.id,
        action: changeAction,
        reason: reason.trim() || undefined
      };

      // Use direct API Gateway URL to bypass CloudFront
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod/api/events/request-change'
        : 'http://localhost:3001/api/events/request-change';
      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        },
        credentials: 'include',
        body: JSON.stringify({ changes: [requestData] })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to submit change request');
      }

      setChangeDialogOpen(false);
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    setChangeDialogOpen(false);
    setSelectedEvent(null);
    setReason('');
    setError(null);
  };

  // Group events by date (show all events)
  const eventsByDate = events.reduce((acc, event) => {
    const date = event.eventDate;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(event);
    return acc;
  }, {} as Record<string, Event[]>);

  const sortedDates = Object.keys(eventsByDate).sort();

  // Dynamic title based on view mode
  const pageTitle = showAllEvents ? 'All Event Schedule' : 'My Event Schedule';

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <EventIcon sx={{ mr: 1 }} />
        {pageTitle}
      </Typography>

      {events.length === 0 ? (
        <Alert severity="info">
          {showAllEvents 
            ? 'No events found. Check back later for updates to your schedule.'
            : 'You are not registered for any events yet. Click on "Total Events" card to see all available events.'}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {sortedDates.map(date => (
            <Grid item xs={12} key={date}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                {formatDate(date)}
              </Typography>
              
              <Grid container spacing={2}>
                {eventsByDate[date].map(event => (
                  <Grid item xs={12} md={6} key={event.id}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        border: event.isMandatory ? '2px solid' : '1px solid',
                        borderColor: event.isMandatory ? 'warning.main' : 'divider',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          paddingTop: '56.25%', // 16:9 aspect ratio
                          position: 'relative',
                          overflow: 'hidden',
                          backgroundColor: '#f5f5f5'
                        }}
                      >
                        <Box
                          component="img"
                          src={getEventImageUrl(event)}
                          alt={`${event.name} event image`}
                          loading="lazy"
                          crossOrigin="anonymous"
                          onError={() => handleImageError(event.id)}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center'
                          }}
                        />
                      </Box>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                          <Typography variant="h6" component="div">
                            {event.name}
                          </Typography>
                          <Box display="flex" gap={1}>
                            {event.isMandatory && (
                              <Chip
                                icon={<StarIcon />}
                                label="Required"
                                color="warning"
                                size="small"
                              />
                            )}
                            {!event.isMandatory && hasPendingRequest(event.id) && (
                              <Chip
                                label="Request Pending"
                                color="warning"
                                size="small"
                              />
                            )}
                            {!event.isMandatory && !hasPendingRequest(event.id) && (
                              <Chip
                                label={event.isRegistered ? 'Registered' : 'Not Registered'}
                                color={event.isRegistered ? 'success' : 'default'}
                                size="small"
                              />
                            )}
                          </Box>
                        </Box>

                        {event.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {event.description}
                          </Typography>
                        )}

                        <Box display="flex" alignItems="center" mb={1}>
                          <ScheduleIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {formatTime(event.startTime)} - {formatTime(event.endTime)}
                          </Typography>
                        </Box>

                        <Box display="flex" alignItems="center" mb={1}>
                          <LocationIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {event.location}
                          </Typography>
                        </Box>

                        {event.dressCode && (
                          <Box 
                            sx={{ 
                              mb: 2, 
                              p: 1.5, 
                              backgroundColor: theme.palette.secondary.light || 'secondary.light', 
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: theme.palette.secondary.main || 'secondary.main'
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.secondary.dark || 'secondary.dark', mb: 0.5 }}>
                              Dress Code
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              {event.dressCode}
                            </Typography>
                          </Box>
                        )}

                        {!event.isMandatory && !hasPendingRequest(event.id) && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleRequestChange(event)}
                            fullWidth
                          >
                            {event.isRegistered ? 'Request to Unregister' : 'Request to Register'}
                          </Button>
                        )}

                        {!event.isMandatory && hasPendingRequest(event.id) && (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            Your change request is pending admin approval
                          </Alert>
                        )}

                        {event.isMandatory && (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            This is a required event - attendance is mandatory
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              {date !== sortedDates[sortedDates.length - 1] && (
                <Divider sx={{ my: 3 }} />
              )}
            </Grid>
          ))}
        </Grid>
      )}

      {/* Change Request Dialog */}
      <Dialog open={changeDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Request Event Change
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Event: {selectedEvent.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {formatDate(selectedEvent.eventDate)} at {formatTime(selectedEvent.startTime)}
              </Typography>

              <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
                <InputLabel>Action</InputLabel>
                <Select
                  value={changeAction}
                  label="Action"
                  onChange={(e) => setChangeAction(e.target.value as 'register' | 'unregister')}
                >
                  <MenuItem value="register">Register for this event</MenuItem>
                  <MenuItem value="unregister">Unregister from this event</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Please provide a reason for this change request..."
                sx={{ mb: 2 }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitChange} 
            variant="contained"
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};